// TMDB API Helper Functions
const TMDB_BASE_URL = "https://api.themoviedb.org/3";

/**
 * Get the TMDB bearer token from environment variables
 */
export const getTMDBToken = (): string => {
  const token = import.meta.env.VITE_TMDB_ACCESS_TOKEN;
  if (!token) {
    throw new Error(
      "TMDB_ACCESS_TOKEN not found. Please set VITE_TMDB_ACCESS_TOKEN in your .env file."
    );
  }
  return token;
};

/**
 * Create authenticated headers for TMDB API requests
 */
export const createTMDBHeaders = (bearerToken?: string): HeadersInit => {
  const token = bearerToken || getTMDBToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

/**
 * Generic TMDB API fetch helper
 */
export const tmdbFetch = async function tmdbFetch(
  url: string,
  options: RequestInit = {},
  bearerToken?: string
) {
  const headers = createTMDBHeaders(bearerToken);

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(
        `TMDB API Error: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Failed to fetch from TMDB API");
  }
};

/**
 * TMDB API endpoints helper
 */
export const TMDB_ENDPOINTS = {
  // Movies
  POPULAR_MOVIES: "/movie/popular",
  NOW_PLAYING_MOVIES: "/movie/now_playing",
  TOP_RATED_MOVIES: "/movie/top_rated",
  UPCOMING_MOVIES: "/movie/upcoming",
  MOVIE_DETAILS: (id: number) => `/movie/${id}`,

  // TV Shows
  POPULAR_TV: "/tv/popular",
  TOP_RATED_TV: "/tv/top_rated",
  TV_DETAILS: (id: number) => `/tv/${id}`,
  TV_SEASON: (tvId: number, seasonNumber: number) =>
    `/tv/${tvId}/season/${seasonNumber}`,

  // Search
  SEARCH_MOVIES: "/search/movie",
  SEARCH_MULTI: "/search/multi",

  // Discover
  DISCOVER_MOVIES: "/discover/movie",
} as const;

/**
 * Static TMDB API URL Templates with Replaceable Parameters
 * Use string replacement or the helper function to fill in parameters
 *
 * Manual replacement examples:
 * - TMDB_ENDPOINTS_STATIC.MOVIES.DETAILS.replace('{tmdb_base_url}', 'https://api.themoviedb.org/3').replace('{id}', '123') → "https://api.themoviedb.org/3/movie/123"
 * - TMDB_ENDPOINTS_STATIC.TV_SHOWS.SEASON_DETAILS.replace('{tmdb_base_url}', 'https://api.themoviedb.org/3').replace('{tvId}', '456').replace('{seasonNumber}', '2') → "https://api.themoviedb.org/3/tv/456/season/2"
 *
 * Using helper function examples (with TMDB base URL):
 * - replaceUrlParams(TMDB_ENDPOINTS_STATIC.MOVIES.DETAILS, { tmdb_base_url: "https://api.themoviedb.org/3", id: 123 }) → "https://api.themoviedb.org/3/movie/123"
 * - replaceUrlParams(TMDB_ENDPOINTS_STATIC.TV_SHOWS.SEASON_DETAILS, { tmdb_base_url: "https://api.themoviedb.org/3", tvId: 456, seasonNumber: 2 }) → "https://api.themoviedb.org/3/tv/456/season/2"
 *
 * Using helper function with custom base URL:
 * - replaceUrlParams(TMDB_ENDPOINTS_STATIC.MOVIES.DETAILS, { tmdb_base_url: "https://custom-api.com", id: 123 }) → "https://custom-api.com/movie/123"
 *
 * Relative URLs only (base URL becomes part of the params):
 * - replaceUrlParamsRelative(TMDB_ENDPOINTS_STATIC.MOVIES.DETAILS, { tmdb_base_url: "", id: 123 }) → "/movie/123"
 *
 * Real-world usage examples:
 *
 * // Fetch movie details with TMDB base URL
 * const movieUrl = replaceUrlParams(TMDB_ENDPOINTS_STATIC.MOVIES.DETAILS, {
 *   tmdb_base_url: "https://api.themoviedb.org/3",
 *   id: 550
 * });
 * // → "https://api.themoviedb.org/3/movie/550"
 *
 * // Fetch TV season with custom API base
 * const seasonUrl = replaceUrlParams(TMDB_ENDPOINTS_STATIC.TV_SHOWS.SEASON_DETAILS, {
 *   tmdb_base_url: "https://my-custom-tmdb-proxy.com/api",
 *   tvId: 1399,
 *   seasonNumber: 1
 * });
 * // → "https://my-custom-tmdb-proxy.com/api/tv/1399/season/1"
 *
 * // Build URLs for batch requests (relative only)
 * const endpoints = [
 *   replaceUrlParamsRelative(TMDB_ENDPOINTS_STATIC.MOVIES.POPULAR, { tmdb_base_url: "" }),
 *   replaceUrlParamsRelative(TMDB_ENDPOINTS_STATIC.TV_SHOWS.TOP_RATED, { tmdb_base_url: "" }),
 *   replaceUrlParamsRelative(TMDB_ENDPOINTS_STATIC.TRENDING.ALL_DAY, { tmdb_base_url: "" })
 * ];
 * // → ["/movie/popular", "/tv/top_rated", "/trending/all/day"]
 *
 * // Environment-specific helper
 * const createApiUrl = (baseUrl: string) => (template: string, params: Record<string, any>) =>
 *   replaceUrlParams(template, { tmdb_base_url: baseUrl, ...params });
 *
 * const prodApi = createApiUrl("https://api.themoviedb.org/3");
 * const stagingApi = createApiUrl("https://staging.themoviedb.org/3");
 *
 * const movieUrl = prodApi(TMDB_ENDPOINTS_STATIC.MOVIES.DETAILS, { id: 550 });
 */

/**
 * Convenience function to create TMDB URLs with the default base URL
 * @param template - URL template from TMDB_ENDPOINTS_STATIC
 * @param params - Parameters to replace (excluding tmdb_base_url)
 * @returns Complete TMDB API URL
 *
 * Usage:
 * createTmdbUrl(TMDB_ENDPOINTS_STATIC.MOVIES.DETAILS, { id: 550 }) → "https://api.themoviedb.org/3/movie/550"
 */
export function createTmdbUrl(
  template: string,
  params: Record<string, string | number>
): string {
  return replaceUrlParams(template, {
    tmdb_base_url: TMDB_BASE_URL,
    ...params,
  });
}
export const TMDB_ENDPOINTS_STATIC = {
  // === MOVIES ===
  MOVIES: {
    POPULAR: "{tmdb_base_url}/movie/popular",
    NOW_PLAYING: "{tmdb_base_url}/movie/now_playing",
    TOP_RATED: "{tmdb_base_url}/movie/top_rated",
    UPCOMING: "{tmdb_base_url}/movie/upcoming",
    LATEST: "{tmdb_base_url}/movie/latest",

    // Dynamic movie endpoints with placeholders
    DETAILS: "{tmdb_base_url}/movie/{id}",
    ALTERNATIVE_TITLES: "{tmdb_base_url}/movie/{id}/alternative_titles",
    CREDITS: "{tmdb_base_url}/movie/{id}/credits",
    EXTERNAL_IDS: "{tmdb_base_url}/movie/{id}/external_ids",
    IMAGES: "{tmdb_base_url}/movie/{id}/images",
    KEYWORDS: "{tmdb_base_url}/movie/{id}/keywords",
    RELEASE_DATES: "{tmdb_base_url}/movie/{id}/release_dates",
    VIDEOS: "{tmdb_base_url}/movie/{id}/videos",
    TRANSLATIONS: "{tmdb_base_url}/movie/{id}/translations",
    RECOMMENDATIONS: "{tmdb_base_url}/movie/{id}/recommendations",
    SIMILAR: "{tmdb_base_url}/movie/{id}/similar",
    REVIEWS: "{tmdb_base_url}/movie/{id}/reviews",
    LISTS: "{tmdb_base_url}/movie/{id}/lists",
    CHANGES: "{tmdb_base_url}/movie/{id}/changes",
    RATINGS: "{tmdb_base_url}/movie/{id}/account_states", // Requires authentication
  },

  // === TV SHOWS ===
  TV_SHOWS: {
    POPULAR: "{tmdb_base_url}/tv/popular",
    TOP_RATED: "{tmdb_base_url}/tv/top_rated",
    ON_THE_AIR: "{tmdb_base_url}/tv/on_the_air",
    AIRING_TODAY: "{tmdb_base_url}/tv/airing_today",
    LATEST: "{tmdb_base_url}/tv/latest",

    // Dynamic TV show endpoints with placeholders
    DETAILS: "{tmdb_base_url}/tv/{id}",
    ALTERNATIVE_TITLES: "{tmdb_base_url}/tv/{id}/alternative_titles",
    CHANGES: "{tmdb_base_url}/tv/{id}/changes",
    CONTENT_RATINGS: "{tmdb_base_url}/tv/{id}/content_ratings",
    CREDITS: "{tmdb_base_url}/tv/{id}/credits",
    EPISODE_GROUPS: "{tmdb_base_url}/tv/{id}/episode_groups",
    EXTERNAL_IDS: "{tmdb_base_url}/tv/{id}/external_ids",
    IMAGES: "{tmdb_base_url}/tv/{id}/images",
    KEYWORDS: "{tmdb_base_url}/tv/{id}/keywords",
    RECOMMENDATIONS: "{tmdb_base_url}/tv/{id}/recommendations",
    REVIEWS: "{tmdb_base_url}/tv/{id}/reviews",
    SCREENED_THEATRICALLY: "{tmdb_base_url}/tv/{id}/screened_theatrically",
    SIMILAR: "{tmdb_base_url}/tv/{id}/similar",
    TRANSLATIONS: "{tmdb_base_url}/tv/{id}/translations",
    VIDEOS: "{tmdb_base_url}/tv/{id}/videos",
    WATCH_PROVIDERS: "{tmdb_base_url}/tv/{id}/watch/providers",
    ACCOUNT_STATES: "{tmdb_base_url}/tv/{id}/account_states", // Requires authentication

    // TV Seasons with placeholders
    SEASON_DETAILS: "{tmdb_base_url}/tv/{tvId}/season/{seasonNumber}",
    SEASON_CREDITS: "{tmdb_base_url}/tv/{tvId}/season/{seasonNumber}/credits",
    SEASON_EXTERNAL_IDS:
      "{tmdb_base_url}/tv/{tvId}/season/{seasonNumber}/external_ids",
    SEASON_IMAGES: "{tmdb_base_url}/tv/{tvId}/season/{seasonNumber}/images",
    SEASON_VIDEOS: "{tmdb_base_url}/tv/{tvId}/season/{seasonNumber}/videos",

    // TV Episodes with placeholders
    EPISODE_DETAILS:
      "{tmdb_base_url}/tv/{tvId}/season/{seasonNumber}/episode/{episodeNumber}",
    EPISODE_CREDITS:
      "{tmdb_base_url}/tv/{tvId}/season/{seasonNumber}/episode/{episodeNumber}/credits",
    EPISODE_EXTERNAL_IDS:
      "{tmdb_base_url}/tv/{tvId}/season/{seasonNumber}/episode/{episodeNumber}/external_ids",
    EPISODE_IMAGES:
      "{tmdb_base_url}/tv/{tvId}/season/{seasonNumber}/episode/{episodeNumber}/images",
    EPISODE_TRANSLATIONS:
      "{tmdb_base_url}/tv/{tvId}/season/{seasonNumber}/episode/{episodeNumber}/translations",
    EPISODE_VIDEOS:
      "{tmdb_base_url}/tv/{tvId}/season/{seasonNumber}/episode/{episodeNumber}/videos",
  },

  // === PEOPLE ===
  PEOPLE: {
    POPULAR: "{tmdb_base_url}/person/popular",
    LATEST: "{tmdb_base_url}/person/latest",

    // Dynamic people endpoints with placeholders
    DETAILS: "{tmdb_base_url}/person/{personId}",
    CHANGES: "{tmdb_base_url}/person/{personId}/changes",
    MOVIE_CREDITS: "{tmdb_base_url}/person/{personId}/movie_credits",
    TV_CREDITS: "{tmdb_base_url}/person/{personId}/tv_credits",
    COMBINED_CREDITS: "{tmdb_base_url}/person/{personId}/combined_credits",
    EXTERNAL_IDS: "{tmdb_base_url}/person/{personId}/external_ids",
    IMAGES: "{tmdb_base_url}/person/{personId}/images",
    TAGGED_IMAGES: "{tmdb_base_url}/person/{personId}/tagged_images",
    TRANSLATIONS: "{tmdb_base_url}/person/{personId}/translations",
  },

  // === SEARCH ===
  SEARCH: {
    MOVIES: "{tmdb_base_url}/search/movie",
    TV_SHOWS: "{tmdb_base_url}/search/tv",
    PEOPLE: "{tmdb_base_url}/search/person",
    MULTI: "{tmdb_base_url}/search/multi",
    COMPANIES: "{tmdb_base_url}/search/company",
    COLLECTIONS: "{tmdb_base_url}/search/collection",
    KEYWORDS: "{tmdb_base_url}/search/keyword",
  },

  // === DISCOVER ===
  DISCOVER: {
    MOVIES: "{tmdb_base_url}/discover/movie",
    TV_SHOWS: "{tmdb_base_url}/discover/tv",
  },

  // === GENRES ===
  GENRES: {
    MOVIE_LIST: "{tmdb_base_url}/genre/movie/list",
    TV_LIST: "{tmdb_base_url}/genre/tv/list",
  },

  // === COLLECTIONS ===
  COLLECTIONS: {
    DETAILS: "{tmdb_base_url}/collection/{collectionId}",
    IMAGES: "{tmdb_base_url}/collection/{collectionId}/images",
    TRANSLATIONS: "{tmdb_base_url}/collection/{collectionId}/translations",
  },

  // === COMPANIES ===
  COMPANIES: {
    DETAILS: "{tmdb_base_url}/company/{companyId}",
    ALTERNATIVE_NAMES: "{tmdb_base_url}/company/{companyId}/alternative_names",
    IMAGES: "{tmdb_base_url}/company/{companyId}/images",
  },

  // === KEYWORDS ===
  KEYWORDS: {
    DETAILS: "{tmdb_base_url}/keyword/{keywordId}",
    MOVIES: "{tmdb_base_url}/keyword/{keywordId}/movies",
  },

  // === NETWORKS ===
  NETWORKS: {
    DETAILS: "{tmdb_base_url}/network/{networkId}",
    ALTERNATIVE_NAMES: "{tmdb_base_url}/network/{networkId}/alternative_names",
    IMAGES: "{tmdb_base_url}/network/{networkId}/images",
  },

  // === REVIEWS ===
  REVIEWS: {
    DETAILS: "{tmdb_base_url}/review/{reviewId}",
  },

  // === TRENDING ===
  TRENDING: {
    ALL_DAY: "{tmdb_base_url}/trending/all/day",
    ALL_WEEK: "{tmdb_base_url}/trending/all/week",
    MOVIE_DAY: "{tmdb_base_url}/trending/movie/day",
    MOVIE_WEEK: "{tmdb_base_url}/trending/movie/week",
    TV_DAY: "{tmdb_base_url}/trending/tv/day",
    TV_WEEK: "{tmdb_base_url}/trending/tv/week",
    PERSON_DAY: "{tmdb_base_url}/trending/person/day",
    PERSON_WEEK: "{tmdb_base_url}/trending/person/week",
  },

  // === CONFIGURATION ===
  CONFIGURATION: {
    API: "{tmdb_base_url}/configuration",
    COUNTRIES: "{tmdb_base_url}/configuration/countries",
    JOBS: "{tmdb_base_url}/configuration/jobs",
    LANGUAGES: "{tmdb_base_url}/configuration/languages",
    PRIMARY_TRANSLATIONS: "{tmdb_base_url}/configuration/primary_translations",
    TIMEZONES: "{tmdb_base_url}/configuration/timezones",
  },

  // === ACCOUNT (Requires Authentication) ===
  ACCOUNT: {
    DETAILS: "{tmdb_base_url}/account",
    FAVORITE_MOVIES: "{tmdb_base_url}/account/favorite/movies",
    FAVORITE_TV: "{tmdb_base_url}/account/favorite/tv",
    LISTS: "{tmdb_base_url}/account/lists",
    LIST_DETAILS: "{tmdb_base_url}/list/{listId}",
    LIST_STATUS: "{tmdb_base_url}/list/{listId}/item_status",
    WATCHLIST_MOVIES: "{tmdb_base_url}/account/watchlist/movies",
    WATCHLIST_TV: "{tmdb_base_url}/account/watchlist/tv",
    RATED_MOVIES: "{tmdb_base_url}/account/rated/movies",
    RATED_TV: "{tmdb_base_url}/account/rated/tv",
    RATED_EPISODES: "{tmdb_base_url}/account/rated/tv/episodes",
  },

  // === CERTIFICATION ===
  CERTIFICATION: {
    MOVIE_CERTIFICATIONS: "{tmdb_base_url}/certification/movie/list",
    TV_CERTIFICATIONS: "{tmdb_base_url}/certification/tv/list",
  },

  // === WATCH PROVIDERS ===
  WATCH_PROVIDERS: {
    MOVIE_REGIONS: "{tmdb_base_url}/watch/providers/movie",
    TV_REGIONS: "{tmdb_base_url}/watch/providers/tv",
    MOVIE_PROVIDERS: "{tmdb_base_url}/watch/providers/movie{?watch_region}",
    TV_PROVIDERS: "{tmdb_base_url}/watch/providers/tv{?watch_region}",
  },

  // === CHANGES ===
  CHANGES: {
    MOVIE: "{tmdb_base_url}/movie/changes",
    TV: "{tmdb_base_url}/tv/changes",
    PERSON: "{tmdb_base_url}/person/changes",
  },

  // === LISTS ===
  LISTS: {
    DETAILS: "{tmdb_base_url}/list/{listId}",
    ITEM_STATUS: "{tmdb_base_url}/list/{listId}/item_status",
  },
} as const;

/**
 * Helper function to replace placeholders in URL templates
 * @param template - URL template with placeholders like {id}
 * @param params - Object with parameter values
 * @param baseUrl - Optional base URL to prepend (defaults to TMDB base URL)
 * @returns Complete URL with replaced parameters
 *
 * Usage:
 * replaceUrlParams("/movie/{id}/credits", { id: 123 }) → "https://api.themoviedb.org/3/movie/123/credits"
 * replaceUrlParams("/tv/{tvId}/season/{seasonNumber}", { tvId: 456, seasonNumber: 2 }) → "https://api.themoviedb.org/3/tv/456/season/2"
 * replaceUrlParams("/movie/{id}", { id: 123 }, "https://custom-api.com") → "https://custom-api.com/movie/123"
 */
export function replaceUrlParams(
  template: string,
  params: Record<string, string | number>
): string {
  let result = template;
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(new RegExp(`\\{${key}\\}`, "g"), value.toString());
  }
  return result;
}
